import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider, useTheme } from "@/contexts/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
import { useAdblockDetector, useSecurityBanDetector } from "@/components/AdblockDetector";

// Pages
import HomePage from "@/pages/HomePage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import PostPage from "@/pages/PostPage";
import ProfilePage from "@/pages/ProfilePage";
import SettingsPage from "@/pages/SettingsPage";
import AdminDashboard from "@/pages/AdminDashboard";
import VerifyAccountPage from "@/pages/VerifyAccountPage";
import ConfidantsPage from "@/pages/ConfidantsPage";
import MembershipPage from "@/pages/MembershipPage";
import SubmitConfessionPage from "@/pages/SubmitConfessionPage";
import ConfessionSubmittedPage from "@/pages/ConfessionSubmittedPage";
import LegalPage from "@/pages/LegalPage";

// Layout
import Layout from "@/components/Layout";

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading, adminTabs, profileComplete } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Profile completion gate: redirect to profile if display_name not set
  if (!profileComplete && !location.pathname.startsWith(`/profile/${user.user_id}`)) {
    return <Navigate to={`/profile/${user.user_id}?setup=1`} replace />;
  }

  if (adminOnly) {
    const isAdmin = user.role === 'admin' || user.role === 'superadmin';
    const hasAnyAdminTab = adminTabs && adminTabs.length > 0;
    if (!isAdmin && !hasAnyAdminTab) {
      return <Navigate to="/" replace />;
    }
  }

  return children;
};

// Syncs user's color_theme from DB to ThemeContext on login/logout
const ThemeSync = () => {
  const { user } = useAuth();
  const { setColorTheme, fetchThemes } = useTheme();
  const prevUserIdRef = React.useRef(null);

  useEffect(() => {
    const userId = user?.user_id || null;
    // Only sync on actual user change (login/logout), not on every re-render
    if (userId !== prevUserIdRef.current) {
      prevUserIdRef.current = userId;
      if (user) {
        setColorTheme(user.color_theme || 'default');
        fetchThemes();
      } else {
        setColorTheme('default');
      }
    }
  }, [user, setColorTheme, fetchThemes]);

  return null;
};

// App Router with adblock check
const AppRouter = () => {
  const adblockDetected = useAdblockDetector();
  const securityBanned = useSecurityBanDetector();

  // Security banned - render nothing (ban overlay is injected via DOM)
  if (securityBanned) {
    return null;
  }

  // Adblock detection is now handled server-side — the feed API returns
  // a locked placeholder post when adblock is detected for non-premium users.
  // No client-side blocking needed.

  return (
    <Routes>
      <Route path="/" element={<><ThemeSync /><Layout /></>}>
        <Route index element={<HomePage />} />
        <Route path="post/:postId" element={<PostPage />} />
        <Route path="profile/:userId" element={<ProfilePage />} />
        <Route path="settings" element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        } />
        <Route path="confidants" element={
          <ProtectedRoute>
            <ConfidantsPage />
          </ProtectedRoute>
        } />
        <Route path="contributors" element={<Navigate to="/?tab=contributors" replace />} />
        <Route path="about" element={<LegalPage type="about" />} />
        <Route path="rules" element={<LegalPage type="rules" />} />
        <Route path="how-it-works" element={<LegalPage type="how-it-works" />} />
        <Route path="faq" element={<LegalPage type="faq" />} />
        <Route path="privacy" element={<LegalPage type="privacy" />} />
        <Route path="terms" element={<LegalPage type="terms" />} />
        <Route path="contact" element={<LegalPage type="contact" />} />
        <Route path="moderation-policy" element={<LegalPage type="moderation" />} />
        <Route path="membership" element={
          <ProtectedRoute>
            <MembershipPage />
          </ProtectedRoute>
        } />
        <Route path="membership/:tier" element={
          <ProtectedRoute>
            <MembershipPage />
          </ProtectedRoute>
        } />
        <Route path="submit-confession" element={
          <ProtectedRoute>
            <SubmitConfessionPage />
          </ProtectedRoute>
        } />
        <Route path="submit-confession/success" element={
          <ProtectedRoute>
            <ConfessionSubmittedPage />
          </ProtectedRoute>
        } />
        <Route path="admin/*" element={
          <ProtectedRoute adminOnly>
            <AdminDashboard />
          </ProtectedRoute>
        } />
      </Route>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/verify-email" element={<Navigate to="/verify-account" replace />} />
      <Route path="/verify-account" element={<VerifyAccountPage />} />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <div className="min-h-screen">
            <div className="grain-overlay" />
            <AppRouter />
            <Toaster position="bottom-right" />
          </div>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
